import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { LogIn } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormData {
  email: string
  password: string
  rememberMe: boolean
}

interface FormErrors {
  email?: string
  password?: string
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
}

export function LoginPage() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!formData.email.trim()) {
      newErrors.email = t('auth.errorRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.errorEmail')
    }
    if (!formData.password.trim()) {
      newErrors.password = t('auth.errorRequired')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      setSubmitted(true)
    }
  }

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <>
      <SEOHead titleKey="seo.login.title" descriptionKey="seo.login.description" />

      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-surface-light py-16 px-4">
        <motion.div {...fadeInUp} className="w-full max-w-md">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-success" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t('auth.loginSuccess')}</h3>
            </motion.div>
          ) : (
            <div className="card p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-2">{t('auth.loginTitle')}</h1>
                <p className="text-text-muted text-sm">{t('auth.loginSubtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-5">
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    {t('auth.emailLabel')} *
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                      errors.email ? 'border-accent' : 'border-surface-border'
                    }`}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                  {errors.email && <p className="text-accent text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="mb-5">
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    {t('auth.passwordLabel')} *
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent ${
                      errors.password ? 'border-accent' : 'border-surface-border'
                    }`}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                  {errors.password && <p className="text-accent text-xs mt-1">{errors.password}</p>}
                </div>

                <div className="flex items-center justify-between mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => handleChange('rememberMe', e.target.checked)}
                      className="w-4 h-4 rounded border-surface-border text-accent focus:ring-accent"
                    />
                    <span className="text-sm text-text-muted">{t('auth.rememberMe')}</span>
                  </label>
                  <button type="button" className="text-sm text-accent hover:underline">
                    {t('auth.forgotPassword')}
                  </button>
                </div>

                <button type="submit" className="btn-primary w-full">
                  {t('auth.signIn')}
                </button>
              </form>

              <p className="text-center text-sm text-text-muted mt-6">
                {t('auth.noAccount')}{' '}
                <LocalizedLink to="register" className="text-accent hover:underline font-medium">
                  {t('auth.signUp')}
                </LocalizedLink>
              </p>
            </div>
          )}
        </motion.div>
      </section>
    </>
  )
}
